import React, { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { getImageUrl, getAdsUrl } from "@/utils/unifiedImageUtils";
// Track loaded image srcs in-memory to avoid flicker on route changes
const loadedSrcSet = new Set<string>();

export type AdOrientation = "horizontal" | "vertical" | "square";

export interface AdItem {
  id?: string | number;
  // Either provide filename (served from /img/banner) or full src
  filename?: string;
  src?: string;
  href?: string;
  alt?: string;
  orientation?: AdOrientation; // optional hint, not required anymore
  aspectRatio?: string; // e.g., '6/1', '4/5', '1/1'
  priority?: boolean; // eager-load to avoid flicker on critical slots
}

interface AdSlotProps extends AdItem {
  className?: string;
  imgClassName?: string;
  rounded?: boolean;
  showPlaceholder?: boolean;
  onImpression?: (id?: string | number) => void;
  onClick?: (id?: string | number) => void;
}

// Infer aspect ratio from explicit prop or filename pattern (e.g., 1440x240)
// Also support name hints like h_, v_, square
const inferAspectRatio = (opts: {
  aspectRatio?: string;
  filename?: string;
  src?: string;
}): string => {
  const { aspectRatio, filename, src } = opts;
  if (aspectRatio) return aspectRatio;
  const s = (filename || src || "").toLowerCase();

  // Try to parse WxH pattern
  const m = s.match(/([0-9]{2,4})x([0-9]{2,4})/);
  if (m) {
    const w = parseInt(m[1], 10);
    const h = parseInt(m[2], 10);
    if (w > 0 && h > 0) {
      const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);
      const g = gcd(w, h) || 1;
      return `${Math.round(w / g)}/${Math.round(h / g)}`;
    }
  }

  // Name hints
  if (/(^|[-_])(v|vertical|portrait)([-_]|$)/.test(s)) return "4/5";
  if (/(^|[-_])(sq|square)([-_]|$)/.test(s)) return "1/1";
  if (/(^|[-_])(h|horizontal|banner)([-_]|$)/.test(s)) return "6/1";

  // Sensible default
  return "6/1";
};

/**
 * AdSlot - Komponen iklan serbaguna (horizontal/vertical/square)
 * - Mendukung link (href), loading state, dan impression tracking
 * - Gunakan filename (disajikan dari /img/banner) atau src absolut
 */
const AdSlot: React.FC<AdSlotProps> = ({
  id,
  filename,
  src,
  href,
  alt = "Advertisement",
  orientation = "horizontal",
  aspectRatio,
  className = "",
  imgClassName = "",
  rounded = true,
  showPlaceholder = true,
  onImpression,
  onClick,
  priority = false,
}) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const hasTrackedRef = useRef(false);

  const resolvedSrc = useMemo(() => {
    if (src) return src;
    if (filename) return getAdsUrl(filename);
    return "/img/noimg.jpg";
  }, [src, filename]);

  // Simplify: derive aspect ratio automatically, orientation becomes optional/hint
  const ratio = useMemo(
    () => inferAspectRatio({ aspectRatio, filename, src }),
    [aspectRatio, filename, src],
  );

  // Intersection observer for impression tracking (once)
  useEffect(() => {
    if (!onImpression) return;
    const el = containerRef.current;
    if (!el || hasTrackedRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTrackedRef.current) {
            hasTrackedRef.current = true;
            onImpression(id);
            observer.disconnect();
          }
        });
      },
      { root: null, threshold: 0.25 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [onImpression, id]);

  const content = (
    <img
      ref={imgRef}
      src={resolvedSrc}
      alt={alt}
      className={clsx(
        "w-full h-full object-cover",
        imgClassName,
        !loaded && "invisible",
      )}
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : undefined}
      decoding={priority ? "sync" : "async"}
      onLoad={() => {
        setLoaded(true);
        if (resolvedSrc) loadedSrcSet.add(resolvedSrc);
      }}
      onError={() => setError(true)}
    />
  );

  // If this src was already loaded earlier, start in loaded state to avoid placeholder flash
  useEffect(() => {
    if (resolvedSrc && loadedSrcSet.has(resolvedSrc)) {
      setLoaded(true);
    }
  }, [resolvedSrc]);

  return (
    <div
      ref={containerRef}
      className={clsx(
        "relative overflow-hidden",
        rounded && "rounded-lg",
        className,
      )}
      style={{ aspectRatio: ratio.replace("/", " / ") }}
    >
      {!loaded && showPlaceholder && (
        <div className="absolute inset-0 w-full h-full bg-gray-200 animate-pulse flex items-center justify-center text-gray-500 text-xs">
          Loading ads...
        </div>
      )}

      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onClick?.(id)}
          className={clsx("block w-full h-full")}
        >
          {content}
        </a>
      ) : (
        content
      )}

      {error && showPlaceholder && (
        <div className="w-full h-full absolute inset-0 bg-gradient-to-r from-gray-100 to-gray-200 p-4 text-center border-2 border-dashed border-gray-300 flex items-center justify-center">
          <div>
            <div className="text-gray-600 font-semibold">
              Advertisement Space
            </div>
            <div className="text-gray-400 text-xs">Banner will appear here</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdSlot;
export type { AdSlotProps };
